import { createContext, useContext, useEffect, useState } from 'react';
import { supabase, Member } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

type AuthContextType = {
  user: User | null;
  member: Member | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string, phone?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshMember: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchMember = async (userId: string) => {
    const { data, error } = await supabase
      .from('members')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error fetching member:', error);
      return null;
    }

    return data;
  };

  const refreshMember = async () => {
    if (user) {
      const memberData = await fetchMember(user.id);
      setMember(memberData);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchMember(session.user.id).then(setMember);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchMember(session.user.id).then(setMember);
      } else {
        setMember(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, name: string, phone?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) throw error;

    if (data.user) {
      const { error: memberError } = await supabase
        .from('members')
        .insert({
          user_id: data.user.id,
          name,
          email,
          phone: phone || null,
          points: 100,
          total_spent: 0,
          member_level: 'regular',
        });

      if (memberError) throw memberError;

      // Fetch the newly created member to get the actual member id
      const { data: newMember } = await supabase
        .from('members')
        .select('id')
        .eq('user_id', data.user.id)
        .maybeSingle();

      if (newMember) {
        const { error: pointError } = await supabase
          .from('point_transactions')
          .insert({
            member_id: newMember.id,
            points: 100,
            type: 'bonus',
            description: '新會員註冊贈送',
          });

        if (pointError) console.error('Error creating point transaction:', pointError);
      }
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, member, loading, signUp, signIn, signOut, refreshMember }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
